import type { AxiosRequestConfig } from 'axios';
import { camelCase, startCase } from 'lodash';
import { awAccountManagementApi } from './Base';
import {
  Comments,
  CommentsResponse,
  ProcessedServicing,
  SearchServiceCasesResponse,
  ServiceType,
  Servicing,
  UapQuery,
} from './models';
import { LogicalOperator, SortOrder } from './types';

export const BASE_SERVICING_PATH = '/service-case/v1/serviceCases';

export const postProcessGetServiceCases = (serviceCasesData: Servicing[]): ProcessedServicing[] =>
  serviceCasesData?.map((serviceCase) => ({
    ...serviceCase,
    actions: {
      rowStatus: serviceCase?.status,
    },
  }));

/**
 * Creates a HashMap from the service type configuration data with category as the key and label as the value.
 * @param {ServiceType[]} configData - The service type configuration data.
 * @returns {Record<string, string>} - A HashMap with category as the key and label as the value.
 */
export const categoryMap = (configData: ServiceType[]): Record<string, string> =>
  configData.reduce<Record<string, string>>((map, item) => {
    map[item.category] = item.label;
    return map;
  }, {});

/**
 * Creates a HashMap from the service type configuration data with unique keys for subCategory.
 * The keys are a combination of category and subCategory to ensure uniqueness.
 * @param {ServiceType[]} configData - The service type configuration data.
 * @returns {Record<string, string>} - A HashMap with unique keys for subCategory and label as the value.
 */
export const subCategoryMap = (configData: ServiceType[]): Record<string, string> =>
  configData.reduce<Record<string, string>>((map, item) => {
    item.subCategories?.forEach((subcategory) => {
      const uniqueKey = subcategory.subCategoryType
        ? `${item.category}:${subcategory.subCategory}:${subcategory.subCategoryType}`
        : `${item.category}:${subcategory.subCategory}`;
      map[uniqueKey] = subcategory.label;
    });
    return map;
  }, {});

export const listQuery: UapQuery = {
  facetPivots: {
    pivotFields: ['status'],
  },
  filters: [
    {
      filters: [
        {
          fieldName: 'status',
          negate: true,
          valueFilter: {
            stringValues: {
              values: ['DRAFT'],
            },
          },
        },
      ],
      operator: LogicalOperator.LOGICAL_OPERATOR_AND,
    },
  ],
  sorts: [
    {
      fieldName: 'createTime',
      sortOrder: SortOrder.SORT_ORDER_DESCENDING,
    },
  ],
};

/**
 * Maps and transforms service case data to include user-friendly category and subcategory labels.
 *
 * @param {ServiceType[]} serviceTypeConfig - The configuration data for service types.
 * @param {Servicing[]} serviceCasesData - The array of service cases to process.
 * @returns {Servicing[]} - The processed array of service cases with mapped category and subcategory labels.
 */
export const postProcessServiceCasesMappings = (serviceTypeConfig: ServiceType[], serviceCasesData: Servicing[]) =>
  serviceCasesData?.map((serviceCase) => {
    const subCategoryKeys =
      serviceCase.subCategories?.map((subCategory) => {
        const typeValue = serviceCase.metadata?.['subCategoryType']?.stringValue;
        return typeValue
          ? `${serviceCase.category}:${subCategory.replaceAll('/', '*')}:${typeValue}`
          : `${serviceCase.category}:${subCategory.replaceAll('/', '*')}`;
      }) ?? [];
    return {
      ...serviceCase,
      clientCaseId: serviceCase?.clientCaseId ?? serviceCase?.clientId,
      category: categoryMap(serviceTypeConfig)[serviceCase.category] || '',
      subCategories: subCategoryKeys.map((key) => subCategoryMap(serviceTypeConfig)[key] || ''),
      status: startCase(camelCase(serviceCase?.status)),
    };
  }) ?? [];

/*
 * To Fetch list of Service case in case of user displaying data by filtering DRAFT status service cases.
 * This is the list of the service cases the user views when no selection is made.
 */
export async function listServiceCases({
  pkg,
  serviceTypeConfig,
  pageToken,
}: {
  pkg: string;
  serviceTypeConfig: ServiceType[];
  pageToken?: string;
}): Promise<{ serviceCases: Servicing[]; nextPageToken: string | undefined; resultsFound: number }> {
  const result = await searchServiceCases({ serviceTypeConfig, query: listQuery, pageSize: 1000, pageToken, pkg });
  return {
    serviceCases: result.serviceCases,
    nextPageToken: result.nextPageToken,
    resultsFound: result.resultsFound,
  };
}

// To Search Service cases based upon search criteria provided
export async function searchServiceCases({
  serviceTypeConfig,
  query,
  pageSize,
  pageToken,
  pkg,
}: {
  serviceTypeConfig?: ServiceType[];
  query?: UapQuery;
  pageSize?: number;
  pageToken?: string;
  pkg: string;
}): Promise<{ serviceCases: Servicing[]; nextPageToken: string | undefined; resultsFound: number }> {
  const requestBody = {
    pkg,
    pageSize,
    pageToken,
    query,
  };
  try {
    const res = await awAccountManagementApi.post<SearchServiceCasesResponse>(
      `${BASE_SERVICING_PATH}:search`,
      requestBody
    );
    // Perform the category & subcategory label mapping here
    const transformedServicingData = postProcessServiceCasesMappings(serviceTypeConfig ?? [], res.data.serviceCases);
    return {
      serviceCases: postProcessGetServiceCases(transformedServicingData),
      nextPageToken: res.data.nextPageToken,
      resultsFound: res.data.resultsFound,
    };
  } catch (error) {
    console.error('Error fetching service cases:', error);
    throw error;
  }
}

// To Fetch an existing Service case
export async function getServiceCase(serviceId: string): Promise<Servicing> {
  try {
    const res = await awAccountManagementApi.get<Servicing>(`${BASE_SERVICING_PATH}/${serviceId}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/*
 * To Create a Service case
 * NOTE: Retry is disabled for this endpoint to prevent duplicate service case creation
 * which could have trading impact. If the request fails, the user must manually retry.
 */
export async function createServiceCase(servicingData: Servicing): Promise<Servicing> {
  try {
    // Axios-retry extends AxiosRequestConfig with 'axios-retry' property at runtime
    const config: AxiosRequestConfig & { 'axios-retry'?: { retries: number } } = {
      'axios-retry': {
        retries: 0,
      },
    };
    const res = await awAccountManagementApi.post<Servicing>(BASE_SERVICING_PATH, servicingData, config);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
// To Update a Service case
export async function updateServiceCase(servicingId: string, servicingData: Servicing): Promise<Servicing> {
  try {
    const res = await awAccountManagementApi.put<Servicing>(`${BASE_SERVICING_PATH}/${servicingId}`, servicingData);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Delete a Service case - Currently, this function is not being used but is kept for potential future use.
export async function deleteServiceCase(serviceId: string): Promise<Servicing> {
  try {
    return await awAccountManagementApi.delete(`${BASE_SERVICING_PATH}/${serviceId}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Update the status of an Service Case
export async function updateServiceCaseStatus(servicingId: string, status: string, pkg: string): Promise<Servicing> {
  try {
    const requestBody = { pkg, status };
    const res = await awAccountManagementApi.post<Servicing>(
      `${BASE_SERVICING_PATH}/${servicingId}:updateStatus`,
      requestBody
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To List Comments of an Servicing Request
export async function listServicingComments(parentId: string): Promise<Comments[]> {
  try {
    const res = await awAccountManagementApi.get<CommentsResponse>(`${BASE_SERVICING_PATH}/${parentId}/comments`);
    return res.data.comments;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Create Comment for a Servicing Request
export async function createCommentServicing(
  parentId: string,
  commentsData: string,
  userId: string | undefined
): Promise<Comments> {
  const commentsBody = {
    content: commentsData.trim(),
    author: userId,
  };
  try {
    const res = await awAccountManagementApi.post<Comments>(
      `${BASE_SERVICING_PATH}/${parentId}/comments`,
      commentsBody
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
